import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../lib/auth";
import { Logo } from "./Logo";

const MENUS = {
  admin: [
    { key: "overview", label: "Overview", icon: "LayoutDashboard" },
    { key: "operations", label: "Operations", icon: "Activity" },
    { key: "leads", label: "Leads", icon: "UserPlus" },
    { key: "chats", label: "Chat Leads", icon: "MessagesSquare" },
    { key: "applications", label: "Applications", icon: "FileText" },
    { key: "users", label: "Users & Team", icon: "Users" },
    { key: "commissions", label: "Commissions", icon: "Coins" },
    { key: "payouts", label: "Payouts", icon: "Banknote" },
    { key: "ranks", label: "Ranks & Rewards", icon: "Trophy" },
    { key: "documents", label: "Documents", icon: "FolderOpen" },
    { key: "content", label: "Website Content", icon: "Newspaper" },
    { key: "integrations", label: "Integrations", icon: "Plug" },
    { key: "settings", label: "Settings", icon: "Settings" },
  ],
  staff: [
    { key: "overview", label: "Overview", icon: "LayoutDashboard" },
    { key: "leads", label: "Leads", icon: "UserPlus" },
    { key: "chats", label: "Chat Leads", icon: "MessagesSquare" },
    { key: "applications", label: "Applications", icon: "FileText" },
    { key: "content", label: "Website Content", icon: "Newspaper" },
  ],
  customer: [
    { key: "overview", label: "My Dashboard", icon: "LayoutDashboard" },
    { key: "apply", label: "Apply for Loan", icon: "FilePlus" },
    { key: "applications", label: "My Applications", icon: "FileText" },
    { key: "documents", label: "My Documents", icon: "FolderOpen" },
  ],
  agent: [
    { key: "overview", label: "Overview", icon: "LayoutDashboard" },
    { key: "add", label: "Add Client", icon: "UserPlus" },
    { key: "applications", label: "Client Applications", icon: "FileText" },
    { key: "commissions", label: "My Commissions", icon: "Coins" },
    { key: "payouts", label: "Payouts", icon: "Banknote" },
    { key: "team", label: "My Team", icon: "Network" },
  ],
  affiliate: [
    { key: "overview", label: "Overview", icon: "LayoutDashboard" },
    { key: "referral", label: "Referral Link", icon: "Link2" },
    { key: "earnings", label: "Earnings", icon: "Coins" },
    { key: "payouts", label: "Payouts", icon: "Banknote" },
    { key: "team", label: "My Team", icon: "Network" },
  ],
};

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export const DashboardLayout = ({ tab, setTab, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menu = MENUS[user?.role] || [];

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col lg:flex-row" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen bg-obsidian-800 border-r border-white/10 flex lg:flex-col shrink-0">
        <div className="p-5 border-b border-white/10 hidden lg:block">
          <Logo />
        </div>
        <nav className="flex lg:flex-col gap-1 p-3 overflow-x-auto lg:overflow-visible flex-1 scrollbar-none">
          {menu.map((m) => (
            <button
              key={m.key}
              onClick={() => setTab(m.key)}
              data-testid={`dash-tab-${m.key}`}
              className={`flex items-center gap-3 px-3.5 lg:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
                tab === m.key ? "gold-gradient text-obsidian-900" : "text-slate-300 hover:bg-white/5"
              }`}
            >
              <Icon name={m.icon} className="w-4 h-4" />
              {m.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 hidden lg:block">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5">
            <Icons.Home className="w-4 h-4" /> Back to Website
          </button>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10">
            <Icons.LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="glass border-b border-white/10 px-5 py-3 flex items-center justify-between sticky top-0 z-30">
          <div>
            <div className="text-xs text-gold uppercase tracking-widest font-semibold capitalize">{user?.role} Panel</div>
            <div className="font-heading font-bold text-white">Welcome, {user?.name?.split(" ")[0]}</div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="w-9 h-9 rounded-full gold-gradient flex items-center justify-center text-obsidian-900 font-bold text-sm">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button onClick={() => { logout(); navigate("/login"); }} className="lg:hidden text-red-400 p-2">
              <Icons.LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 max-w-6xl">{children}</main>
      </div>
    </div>
  );
};
